<script lang="ts">
  import { tick } from 'svelte';
  import type { Task, TaskPriority, TaskStatus } from '../lib/types';
  import { setProjectTasks } from '../lib/stores/projectTasksCache';
  import {
    addTask,
    completeTask,
    toggleTaskActive,
    deleteTask as deleteTaskApi,
    markTaskWontDo,
  } from '../lib/taskApi';
  import { jsonHeaders } from '../lib/apiHelpers';
  import { recentlyAddedTasks } from '../lib/stores/recentlyAddedTask';
  import { setCompactTaskDragImage } from '../lib/dragImage';
  import TaskDetailPopover from './TaskDetailPopover.svelte';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    tasks: Task[];
    projectId: string;
    /** Shown in the task popover's breadcrumb. */
    project?: { title: string; category?: string | null } | null;
    onTasksChange: (tasks: Task[]) => void;
  }

  let { tasks, projectId, project = null, onTasksChange }: Props = $props();

  // ─── Search ───────────────────────────────────────────────────
  let searchQuery = $state('');

  function highlightSegments(text: string, query: string): { text: string; highlight: boolean }[] {
    if (!query.trim()) return [{ text, highlight: false }];
    const lower = text.toLowerCase();
    const q = query.toLowerCase().trim();
    const segments: { text: string; highlight: boolean }[] = [];
    let lastIdx = 0;
    let idx = lower.indexOf(q, lastIdx);
    while (idx !== -1) {
      if (idx > lastIdx) segments.push({ text: text.slice(lastIdx, idx), highlight: false });
      segments.push({ text: text.slice(idx, idx + q.length), highlight: true });
      lastIdx = idx + q.length;
      idx = lower.indexOf(q, lastIdx);
    }
    if (lastIdx < text.length) segments.push({ text: text.slice(lastIdx), highlight: false });
    return segments;
  }

  let filteredTasks = $derived.by(() => {
    if (!searchQuery.trim()) return tasks;
    const q = searchQuery.toLowerCase().trim();
    return tasks.filter(
      (t) => t.title.toLowerCase().includes(q) || t.notes?.toLowerCase().includes(q),
    );
  });

  // ─── Kanban lanes ─────────────────────────────────────────────
  let todoTasks = $derived(
    filteredTasks
      .filter((t) => t.status === 'todo' && !t.isActive)
      .sort(
        (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority] || a.position - b.position,
      ),
  );
  let activeTasks = $derived(
    filteredTasks
      .filter((t) => t.status === 'todo' && t.isActive)
      .sort(
        (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority] || a.position - b.position,
      ),
  );
  let doneTasks = $derived(
    filteredTasks
      .filter((t) => t.status === 'done' || t.status === 'wont_do')
      .sort((a, b) => {
        const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return bTime - aTime;
      }),
  );

  // ─── Grouping ─────────────────────────────────────────────────
  type GroupBy = 'none' | 'priority';
  let groupBy = $state<GroupBy>('none');

  const priorityOrder: Record<TaskPriority, number> = { high: 0, medium: 1, low: 2, none: 3 };
  const priorityLabels: Record<TaskPriority, string> = {
    high: '🔴 High',
    medium: '🟠 Medium',
    low: '🔵 Low',
    none: '⚪ No Priority',
  };

  function groupTaskList(taskList: Task[]): { label: string; key: string; tasks: Task[] }[] {
    if (groupBy === 'priority') {
      const groups: Record<string, Task[]> = { high: [], medium: [], low: [], none: [] };
      for (const t of taskList) {
        groups[t.priority].push(t);
      }
      return (['high', 'medium', 'low', 'none'] as TaskPriority[])
        .filter((p) => groups[p].length > 0)
        .map((p) => ({
          label: priorityLabels[p],
          key: p,
          tasks: groups[p].sort((a, b) => a.position - b.position),
        }));
    }
    // flat list
    return [{ label: '', key: 'all', tasks: taskList }];
  }

  let todoGrouped = $derived(groupTaskList(todoTasks));
  let activeGrouped = $derived(groupTaskList(activeTasks));
  let doneGrouped = $derived(groupTaskList(doneTasks));

  // ─── Priority colors ─────────────────────────────────────────
  const priorityDotColors: Record<TaskPriority, string> = {
    high: '#ef4444',
    medium: '#fb923c',
    low: '#60a5fa',
    none: '#d1d5db',
  };

  // ─── Actions ──────────────────────────────────────────────────
  async function handleComplete(task: Task) {
    const result = await completeTask(projectId, task.id, tasks);
    onTasksChange(result.tasks);
  }

  async function handleToggleActive(task: Task, active?: boolean) {
    const result = await toggleTaskActive(projectId, task.id, tasks, active);
    onTasksChange(result.tasks);
  }

  // ─── Task Detail Popover ──────────────────────────────────────
  let popoverTask = $state<Task | null>(null);
  let popoverAnchorEl = $state<HTMLElement | null>(null);

  function openTaskPopover(task: Task, anchorEl: HTMLElement) {
    if (popoverTask?.id === task.id) {
      closeTaskPopover();
      return;
    }
    popoverTask = task;
    popoverAnchorEl = anchorEl;
  }

  function closeTaskPopover() {
    popoverTask = null;
    popoverAnchorEl = null;
  }

  async function handlePopoverUpdate(task: Task, updates: Partial<Task>) {
    const prevTasks = [...tasks];

    // Optimistic update
    const newTasks = tasks.map((t) => (t.id === task.id ? { ...t, ...updates } : t));
    onTasksChange(newTasks);
    setProjectTasks(projectId, newTasks);

    try {
      const response = await fetch(`/projects/${projectId}/tasks/${task.id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ task: updates }),
      });
      if (response.ok) {
        const updated: Task = await response.json();
        const finalTasks = tasks.map((t) => (t.id === task.id ? updated : t));
        onTasksChange(finalTasks);
        setProjectTasks(projectId, finalTasks);
        if (popoverTask?.id === task.id) {
          popoverTask = updated;
        }
      } else {
        onTasksChange(prevTasks);
        setProjectTasks(projectId, prevTasks);
      }
    } catch {
      onTasksChange(prevTasks);
      setProjectTasks(projectId, prevTasks);
    }
  }

  async function handlePopoverDelete(task: Task) {
    closeTaskPopover();
    const result = await deleteTaskApi(projectId, task.id, tasks);
    onTasksChange(result.tasks);
  }

  async function handlePopoverMarkWontDo(task: Task, pendingUpdates?: Partial<Task>) {
    closeTaskPopover();
    if (pendingUpdates) {
      const merged = tasks.map((t) => (t.id === task.id ? { ...t, ...pendingUpdates } : t));
      onTasksChange(merged);
      setProjectTasks(projectId, merged);
    }
    const currentTasks = pendingUpdates
      ? tasks.map((t) => (t.id === task.id ? { ...t, ...pendingUpdates } : t))
      : tasks;
    const result = await markTaskWontDo(projectId, task.id, currentTasks, pendingUpdates);
    onTasksChange(result.tasks);
  }

  async function handlePopoverToggleComplete(task: Task, pendingUpdates?: Partial<Task>) {
    closeTaskPopover();
    if (pendingUpdates) {
      const merged = tasks.map((t) => (t.id === task.id ? { ...t, ...pendingUpdates } : t));
      onTasksChange(merged);
      setProjectTasks(projectId, merged);
      await handlePopoverUpdate(task, pendingUpdates);
    }
    const result = await completeTask(projectId, task.id, tasks);
    onTasksChange(result.tasks);
  }

  // ─── Drag and drop ────────────────────────────────────────────
  let draggedTask = $state<Task | null>(null);
  let dropLane = $state<string | null>(null);

  function handleDragStart(e: DragEvent, task: Task) {
    draggedTask = task;
    e.dataTransfer!.effectAllowed = 'move';
    e.dataTransfer!.setData('text/plain', task.id);
    setCompactTaskDragImage(e, task.title, task.priority);
  }

  function handleDragEnd() {
    draggedTask = null;
    dropLane = null;
  }

  function handleDragOver(e: DragEvent, lane: string) {
    e.preventDefault();
    dropLane = lane;
  }

  function handleDragLeave() {
    dropLane = null;
  }

  async function handleDrop(e: DragEvent, lane: string) {
    e.preventDefault();
    dropLane = null;
    if (!draggedTask) return;

    const task = draggedTask;
    draggedTask = null;

    if (lane === 'done' && task.status === 'todo') {
      await handleComplete(task);
    } else if (lane === 'active' && task.status === 'todo' && !task.isActive) {
      await handleToggleActive(task, true);
    } else if (lane === 'active' && (task.status === 'done' || task.status === 'wont_do')) {
      // Reopen + pin
      const result = await completeTask(projectId, task.id, tasks);
      onTasksChange(result.tasks);
      const result2 = await toggleTaskActive(projectId, task.id, result.tasks, true);
      onTasksChange(result2.tasks);
    } else if (lane === 'todo' && task.isActive) {
      await handleToggleActive(task, false);
    } else if (lane === 'todo' && (task.status === 'done' || task.status === 'wont_do')) {
      await handleComplete(task);
    }
  }

  // ─── Lane config ──────────────────────────────────────────────
  const lanes = [
    { key: 'todo', label: 'To Do', icon: '📋', accent: '#3b82f6' },
    { key: 'active', label: 'Working On', icon: '📌', accent: '#f59e0b' },
    { key: 'done', label: 'Done', icon: '✅', accent: '#22c55e' },
  ] as const;

  function getLaneTasks(key: string) {
    switch (key) {
      case 'todo':
        return todoGrouped;
      case 'active':
        return activeGrouped;
      case 'done':
        return doneGrouped;
      default:
        return [];
    }
  }

  function getLaneCount(key: string) {
    switch (key) {
      case 'todo':
        return todoTasks.length;
      case 'active':
        return activeTasks.length;
      case 'done':
        return doneTasks.length;
      default:
        return 0;
    }
  }

  // ─── Add task in a lane ───────────────────────────────────────
  type LaneKey = (typeof lanes)[number]['key'];
  let addingLane = $state<LaneKey | null>(null);
  let newLaneTaskTitle = $state('');
  let newLaneTaskInput: HTMLInputElement | undefined = $state();
  let isAddingLaneTask = $state(false);

  async function startAddingInLane(lane: LaneKey) {
    if (addingLane === lane) {
      await tick();
      newLaneTaskInput?.focus();
      return;
    }
    addingLane = lane;
    newLaneTaskTitle = '';
    await tick();
    newLaneTaskInput?.focus();
  }

  function cancelAddingInLane() {
    addingLane = null;
    newLaneTaskTitle = '';
  }

  async function addTaskInLane(lane: LaneKey) {
    const title = newLaneTaskTitle.trim();
    if (!title || isAddingLaneTask) return;

    isAddingLaneTask = true;
    newLaneTaskTitle = '';

    const options =
      lane === 'active'
        ? { isActive: true }
        : lane === 'done'
          ? { status: 'done' as TaskStatus }
          : undefined;

    try {
      const result = await addTask(projectId, title, tasks, options);
      onTasksChange(result.tasks);
    } finally {
      isAddingLaneTask = false;
      await tick();
      newLaneTaskInput?.focus();
    }
  }
</script>

<div class="project-kanban">
  <!-- Controls bar -->
  <div class="kanban-controls">
    <div class="controls-left">
      <span class="task-stats">
        {#if searchQuery.trim()}
          {filteredTasks.length} of {tasks.length} tasks
        {:else}
          {tasks.filter((t) => t.status === 'todo').length} open · {tasks.filter(
            (t) => t.status !== 'todo',
          ).length} done
        {/if}
      </span>
    </div>
    <div class="controls-right">
      <div class="search-wrapper">
        <svg class="search-icon" width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
          <path
            d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242.156a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z"
          />
        </svg>
        <input
          autocomplete="off"
          type="text"
          class="search-input"
          placeholder="Search tasks…"
          bind:value={searchQuery}
          onkeydown={(e) => {
            if (e.key === 'Escape') searchQuery = '';
          }}
        />
        <button
          type="button"
          class="search-clear"
          class:visible={searchQuery !== ''}
          onclick={() => (searchQuery = '')}
          aria-label="Clear search"
          disabled={searchQuery === ''}
        >
          ✕
        </button>
      </div>
      <div class="group-tabs" role="tablist" aria-label="Group tasks by">
        {#each [{ value: 'none', label: 'All' }, { value: 'priority', label: 'Priority' }] as tab (tab.value)}
          <button
            type="button"
            role="tab"
            class="group-tab"
            class:active={groupBy === tab.value}
            aria-selected={groupBy === tab.value}
            onclick={() => (groupBy = tab.value as GroupBy)}
          >
            {tab.label}
          </button>
        {/each}
      </div>
    </div>
  </div>

  <!-- Kanban Board -->
  <div class="kanban-board">
    {#each lanes as lane (lane.key)}
      {@const groups = getLaneTasks(lane.key)}
      {@const count = getLaneCount(lane.key)}

      <div
        class="kanban-lane"
        class:drop-target={dropLane === lane.key}
        style="--lane-accent: {lane.accent};"
        role="group"
        aria-label="{lane.label} tasks"
        ondragover={(e) => handleDragOver(e, lane.key)}
        ondragleave={handleDragLeave}
        ondrop={(e) => handleDrop(e, lane.key)}
      >
        <div class="lane-header">
          <span class="lane-icon">{lane.icon}</span>
          <span class="lane-title">{lane.label}</span>
          <span class="lane-count">{count}</span>
          <button
            type="button"
            class="lane-add-btn"
            class:active={addingLane === lane.key}
            onclick={() => startAddingInLane(lane.key)}
            aria-label="Add task to {lane.label}"
            title="Add task"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path
                d="M8 2a1 1 0 011 1v4h4a1 1 0 110 2H9v4a1 1 0 11-2 0V9H3a1 1 0 110-2h4V3a1 1 0 011-1z"
              />
            </svg>
          </button>
        </div>

        <div class="lane-content" class:lane-grid={lane.key === 'todo' || lane.key === 'active'}>
          {#if addingLane === lane.key}
            <form
              class="lane-add-form"
              onsubmit={(e) => {
                e.preventDefault();
                addTaskInLane(lane.key);
              }}
            >
              <input
                autocomplete="off"
                bind:this={newLaneTaskInput}
                bind:value={newLaneTaskTitle}
                type="text"
                class="lane-add-input"
                placeholder="Task title…"
                aria-label="New {lane.label} task title"
                onkeydown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelAddingInLane();
                  }
                }}
              />
              <button
                type="submit"
                class="lane-add-submit"
                disabled={!newLaneTaskTitle.trim() || isAddingLaneTask}
              >
                Add
              </button>
            </form>
          {/if}

          {#each groups as group (group.key)}
            {#if group.label}
              <div class="group-header">{group.label}</div>
            {/if}

            {#each group.tasks as task (task.id)}
              <div
                class="task-card {$recentlyAddedTasks.has(task.id) ? 'just-added-task' : ''}"
                class:is-dragging={draggedTask?.id === task.id}
                class:is-done={task.status === 'done' || task.status === 'wont_do'}
                class:is-wont-do={task.status === 'wont_do'}
                role="listitem"
                draggable={!$viewport.isMobile}
                ondragstart={(e) => handleDragStart(e, task)}
                ondragend={handleDragEnd}
              >
                <div class="card-top-row">
                  <button
                    type="button"
                    class="task-checkbox"
                    class:checked={task.status !== 'todo'}
                    class:wont-do={task.status === 'wont_do'}
                    style="border-color: {task.status === 'todo'
                      ? priorityDotColors[task.priority]
                      : '#22c55e'};"
                    onclick={() => handleComplete(task)}
                    title={task.status === 'todo' ? 'Complete task' : 'Reopen task'}
                  >
                    {#if task.status === 'wont_do'}
                      ✕
                    {:else if task.status === 'done'}
                      ✓
                    {/if}
                  </button>

                  <span
                    class="task-title"
                    title={task.title}
                    role="button"
                    tabindex="0"
                    onclick={(e) => openTaskPopover(task, e.currentTarget as HTMLElement)}
                    onkeydown={(e) => {
                      if (e.key === 'Enter') openTaskPopover(task, e.currentTarget as HTMLElement);
                    }}
                  >
                    {#each highlightSegments(task.title, searchQuery) as seg, i (i)}
                      {#if seg.highlight}
                        <mark class="search-highlight">{seg.text}</mark>
                      {:else}
                        {seg.text}
                      {/if}
                    {/each}</span
                  >

                  {#if task.priority !== 'none'}
                    <span
                      class="priority-dot"
                      style="background: {priorityDotColors[task.priority]};"
                      title="{task.priority} priority"
                    ></span>
                  {/if}

                  {#if task.status === 'todo'}
                    <button
                      type="button"
                      class="pin-btn"
                      class:pinned={task.isActive}
                      onclick={() => handleToggleActive(task)}
                      title={task.isActive ? 'Unpin from Working On' : 'Pin to Working On'}
                    >
                      📌
                    </button>
                  {/if}
                </div>

                {#if task.notes}
                  <div class="card-bottom-row">
                    <span class="has-notes" title="Has notes">📝</span>
                  </div>
                {/if}
              </div>
            {/each}
          {/each}

          {#if count === 0 && addingLane !== lane.key}
            <div class="lane-empty">
              <span class="empty-icon">{lane.icon}</span>
              <span class="empty-text">
                {#if lane.key === 'active'}
                  Drag tasks here or click 📌
                {:else if lane.key === 'done'}
                  No completed tasks yet
                {:else}
                  No tasks
                {/if}
              </span>
              <button
                type="button"
                class="empty-add-btn"
                onclick={() => startAddingInLane(lane.key)}
              >
                + Add a task
              </button>
            </div>
          {/if}
        </div>
      </div>
    {/each}
  </div>
</div>

<!-- Task detail popover -->
<TaskDetailPopover
  task={popoverTask}
  anchorEl={popoverAnchorEl}
  {project}
  onClose={closeTaskPopover}
  onUpdate={handlePopoverUpdate}
  onDelete={(task, pendingUpdates) => {
    if (pendingUpdates) handlePopoverUpdate(task, pendingUpdates);
    handlePopoverDelete(task);
  }}
  onMarkWontDo={handlePopoverMarkWontDo}
  onToggleComplete={handlePopoverToggleComplete}
/>

<style>
  .project-kanban {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1;
    min-height: 0;
  }

  /* ─── Controls ─── */
  .kanban-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }

  .controls-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .task-stats {
    font-size: 0.6875rem;
    color: #64748b;
    font-weight: 500;
  }

  .controls-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  /* ─── Group Tabs ─── */
  .group-tabs {
    display: flex;
    background: #f1f5f9;
    border-radius: 0.375rem;
    padding: 0.125rem;
    gap: 0.0625rem;
  }

  .group-tab {
    padding: 0.25rem 0.625rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.12s;
    white-space: nowrap;
  }

  .group-tab:hover {
    color: #334155;
    background: rgba(255, 255, 255, 0.5);
  }

  .group-tab.active {
    color: #1e293b;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    font-weight: 600;
  }

  /* ─── Kanban Board ─── */
  .kanban-board {
    display: flex;
    gap: 0.75rem;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  /* ─── Lane ─── */
  .kanban-lane {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #f8fafc;
    border-radius: 0.5rem;
    overflow: hidden;
    min-width: 0;
    min-height: 0;
    position: relative;
    border: 1px solid #e2e8f0;
    transition: all 0.15s;
  }

  .kanban-lane::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--lane-accent);
  }

  .kanban-lane.drop-target {
    background: #eff6ff !important;
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }

  /* To Do: 2/5, Working On: 2/5, Done: 1/5 */
  .kanban-lane:first-child,
  .kanban-lane:nth-child(2) {
    flex: 2;
  }

  .kanban-lane:last-child {
    flex: 1;
  }

  .lane-header {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.625rem;
    padding-top: 0.625rem;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
    background: white;
  }

  .lane-icon {
    font-size: 0.8125rem;
  }

  .lane-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #334155;
  }

  .lane-count {
    font-size: 0.625rem;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    padding: 0.0625rem 0.375rem;
    border-radius: 1rem;
    min-width: 1.25rem;
    text-align: center;
  }

  .lane-add-btn {
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.375rem;
    height: 1.375rem;
    padding: 0;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.12s;
  }

  .lane-add-btn:hover {
    color: #1e293b;
    background: #f1f5f9;
  }

  .lane-add-btn.active {
    color: #1d4ed8;
    background: #eff6ff;
  }

  .lane-add-btn:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 1px;
  }

  .lane-add-form {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem;
    background: white;
    border: 1px solid #bfdbfe;
    border-radius: 0.375rem;
    box-shadow: 0 1px 2px rgba(59, 130, 246, 0.08);
  }

  .lane-add-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.75rem;
    color: #334155;
    padding: 0.125rem 0.25rem;
  }

  .lane-add-input:focus,
  .lane-add-input:focus-visible {
    outline: none;
    box-shadow: none;
  }

  .lane-add-input::placeholder {
    color: #94a3b8;
  }

  .lane-add-submit {
    flex-shrink: 0;
    padding: 0.1875rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: white;
    background: #2563eb;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .lane-add-submit:hover:not(:disabled) {
    background: #1d4ed8;
  }

  .lane-add-submit:disabled {
    opacity: 0.45;
    cursor: default;
  }

  .lane-content {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    padding: 0.5rem;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }

  .lane-content::-webkit-scrollbar {
    width: 5px;
  }

  .lane-content::-webkit-scrollbar-track {
    background: transparent;
  }

  .lane-content::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  /* ─── Group Header ─── */
  .group-header {
    font-size: 0.625rem;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.375rem 0.25rem 0.125rem;
    border-bottom: 1px solid #f1f5f9;
    margin-top: 0.25rem;
  }

  .group-header:first-child {
    margin-top: 0;
  }

  /* ─── Task Card ─── */
  .task-card {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.5rem 0.625rem;
    background: white;
    border: 1px solid #e8ecf0;
    border-radius: 0.375rem;
    cursor: grab;
    transition: all 0.12s ease;
    min-width: 0;
  }

  .task-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }

  .task-card:active {
    cursor: grabbing;
  }

  .task-card.is-dragging {
    opacity: 0.4;
    transform: scale(0.97);
  }

  .task-card.is-done {
    opacity: 0.65;
  }

  .task-card.is-done .task-title {
    text-decoration: line-through;
    color: #94a3b8;
  }

  .task-card.is-wont-do .task-title {
    text-decoration: line-through;
    color: #cbd5e1;
  }

  /* ─── Card Top Row ─── */
  .card-top-row {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    min-width: 0;
  }

  .task-checkbox {
    width: 1rem;
    height: 1rem;
    min-width: 1rem;
    border-radius: 0.1875rem;
    border: 2px solid #d1d5db;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.5625rem;
    font-weight: 700;
    color: white;
    transition: all 0.12s;
    padding: 0;
  }

  .task-checkbox:hover {
    transform: scale(1.1);
  }

  .task-checkbox.checked {
    background: #22c55e;
    border-color: #22c55e;
  }

  .task-checkbox.wont-do {
    background: #94a3b8;
    border-color: #94a3b8;
  }

  .task-title {
    flex: 1;
    font-size: 0.75rem;
    font-weight: 500;
    color: #334155;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--title-lines, 2);
    line-clamp: var(--title-lines, 2);
    line-height: 1.4;
    cursor: pointer;
    border-radius: 0.125rem;
    padding: 0.0625rem 0.125rem;
    margin: -0.0625rem -0.125rem;
    transition: background-color 0.1s;
    word-break: break-word;
  }

  .task-title:hover {
    background: rgba(59, 130, 246, 0.06);
    color: #1e40af;
  }

  :global(.search-highlight) {
    background: #fef08a;
    color: #854d0e;
    border-radius: 0.125rem;
    padding: 0 0.0625rem;
  }

  .priority-dot {
    width: 0.4375rem;
    height: 0.4375rem;
    min-width: 0.4375rem;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .pin-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.6875rem;
    padding: 0.125rem;
    opacity: 0;
    transition: opacity 0.12s;
    flex-shrink: 0;
  }

  .task-card:hover .pin-btn {
    opacity: 0.5;
  }

  .task-card:hover .pin-btn:hover {
    opacity: 1;
  }

  .pin-btn.pinned {
    opacity: 1 !important;
  }

  /* Touch: no hover to reveal the pin, so unpinned pins stay visible but dim */
  @media (hover: none) and (pointer: coarse) {
    .pin-btn {
      opacity: 0.4;
    }
  }

  /* ─── Card Bottom Row ─── */
  .card-bottom-row {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding-left: 1.375rem;
  }

  .has-notes {
    font-size: 0.5625rem;
    opacity: 0.5;
  }

  /* ─── Empty State ─── */
  .lane-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 2rem 1rem;
    text-align: center;
    color: #94a3b8;
    border: 1.5px dashed #e2e8f0;
    border-radius: 0.375rem;
    background: white;
  }

  .empty-icon {
    font-size: 1.5rem;
    opacity: 0.4;
  }

  .empty-text {
    font-size: 0.6875rem;
    font-weight: 500;
  }

  .empty-add-btn {
    margin-top: 0.125rem;
    padding: 0.1875rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #2563eb;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
  }

  .empty-add-btn:hover {
    background: #eff6ff;
  }

  /* ─── Two-column grid for To Do / Working On ─── */
  .lane-content.lane-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    align-content: start;
    --title-lines: 2;
  }

  .lane-content.lane-grid .group-header {
    grid-column: 1 / -1;
  }

  .lane-content.lane-grid .lane-empty,
  .lane-content.lane-grid .lane-add-form {
    grid-column: 1 / -1;
  }

  /* Single-column lanes (done) get 3 lines */
  .lane-content:not(.lane-grid) {
    --title-lines: 3;
  }

  @media (max-width: 1200px) {
    .lane-content.lane-grid {
      grid-template-columns: 1fr;
      --title-lines: 3;
    }
  }

  /* ─── Search ─── */
  .search-wrapper {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.375rem;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    transition: border-color 0.15s;
  }

  .search-wrapper:focus-within {
    border-color: #2563eb;
  }

  .search-icon {
    color: #9ca3af;
    flex-shrink: 0;
  }

  .search-wrapper:focus-within .search-icon {
    color: #60a5fa;
  }

  .search-input {
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.6875rem;
    color: #374151;
    width: 8rem;
    padding: 0;
    -webkit-appearance: none;
    appearance: none;
  }

  .search-input:focus,
  .search-input:focus-visible {
    outline: none;
    box-shadow: none;
  }

  .search-input::placeholder {
    color: #9ca3af;
  }

  .search-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.875rem;
    height: 0.875rem;
    font-size: 0.5rem;
    color: #9ca3af;
    background: #f1f5f9;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
    opacity: 0;
    pointer-events: none;
    transition: all 0.1s;
  }

  .search-clear.visible {
    opacity: 1;
    pointer-events: auto;
  }

  .search-clear:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 1px;
  }

  .search-clear:hover {
    color: #374151;
    background: #e2e8f0;
  }

  /* ─── Responsive ─── */
  @media (max-width: 768px) {
    .kanban-board {
      flex-direction: column;
      overflow-y: auto;
    }

    .kanban-lane {
      flex: none !important;
      min-height: 8rem;
    }

    .kanban-lane:last-child {
      flex: none;
    }

    .lane-content.lane-grid {
      grid-template-columns: 1fr;
      --title-lines: 3;
    }
  }
</style>
